import { AssetNumberLookup, FillerMatrixLookup, LotNumberLookup } from "./Lookups";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PageButton, PageControls } from "../../../common/PageControls";
import { SaveChangesDialog } from "../../../common/Dialogs";
import { String } from "@/util";
import { useContext } from "react";
import { useGetProcesses, useSave } from "../queries";
import { useModalDialog } from "@/hooks";
import { WelderContext } from "../reducer";
import { WelderRow } from "@/api/hitachi/rbi";

export const MainInterface = () => {
  const { state, updateState } = useContext(WelderContext);
  const { isLoading } = useGetProcesses(state, updateState);

  const save = useSave();
  const dialog = useModalDialog();

  if (isLoading) {
    return <p>Loading welds...</p>;
  }

  function isGroupRow(row: WelderRow) {
    const index = state.processes.indexOf(row);
    return index === 0 || row.weldId !== state.processes[index - 1].weldId;
  }

  function handleSave() {
    save.mutate({ processes: state.processes, redirect: false });
  }

  function handleSaveAndFinish() {
    dialog.show(SaveChangesDialog, {
      onAccept: () => save.mutate({ processes: state.processes, redirect: true }),
    });
  }

  function handleFieldChanged(index: number, field: string, value: string | boolean) {
    const processes = [...state.processes];
    processes[index] = { ...processes[index], [field]: value };
    updateState({
      type: "Processes",
      payload: processes,
    });
  }

  function handleRepairProcessChanged(index: number, process: string) {
    //set wps number when process is changed
    const newProcess = process;
    let newWpsNumber = "";

    if (newProcess === "141: TIG(solid)") {
      newWpsNumber = "34";
    } else if (newProcess === "Mechanical") {
      newWpsNumber = "";
    }

    // Update both fields in one state change
    const processes = [...state.processes];
    processes[index] = {
      ...processes[index],
      process: newProcess,
      wpsNumber: newWpsNumber,
    };
    updateState({
      type: "Processes",
      payload: processes,
    });
  }

  function handleCopy(field: string, value: string) {
    updateState({
      type: "Processes",
      payload: state.processes.map((process) => {
        if (!process.locked) {
          return { ...process, [field]: value };
        }
        return process;
      }),
    });
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover text-center text-nowrap">
          <thead>
            <tr>
              <th>Weld Map</th>
              <th>Drawing No.</th>
              <th>Weld No.</th>
              <th>Process</th>
              <th>WPS</th>
              <th>Filler</th>
              {state.processes.length > 1 && <th></th>}
              <th>Lot No.</th>
              {state.processes.length > 1 && <th></th>}
              <th>Weld Asset No.</th>
              {state.processes.length > 1 && <th></th>}
              <th>Unfinished</th>
              <th>Finished</th>
            </tr>
          </thead>
          <tbody>
            {state.processes.map((row, index) => (
              <tr key={index}>
                <th>{row.weldMap}</th>
                <th>{row.drawingNumber}</th>
                <th>{isGroupRow(row) && row.weldNumber}</th>
                {row.process === "141: TIG(solid)" || row.process === "Mechanical" ? (
                  <td>
                    <select
                      name="process"
                      className="form-select"
                      value={row.process ?? String.Empty}
                      disabled={String.isNullOrWhiteSpace(row.process) || row.locked}
                      onChange={(e) => {
                        handleRepairProcessChanged(index, e.currentTarget.value);
                      }}
                    >
                      <option>141: TIG(solid)</option>
                      <option>Mechanical</option>
                    </select>
                  </td>
                ) : (
                  <td>{row.process}</td>
                )}
                <td>{row.wpsNumber}</td>
                <td>
                  <FillerMatrixLookup
                    process={row.process}
                    wpsNumber={row.wpsNumber}
                    value={row.filler}
                    onChange={(e) => handleFieldChanged(index, "filler", e.currentTarget.value)}
                    disabled={String.isNullOrWhiteSpace(row.process) || row.locked}
                  />
                </td>
                {state.processes.length > 1 && (
                  <td className="align-middle p-0">
                    {index === 0 && (
                      <FontAwesomeIcon
                        icon={faArrowDown}
                        size="sm"
                        type="button"
                        title="Copy"
                        onClick={() => handleCopy("filler", row.filler)}
                      />
                    )}
                  </td>
                )}
                <td>
                  <LotNumberLookup
                    process={row.process}
                    classification={row.filler}
                    value={row.lotNumber}
                    onChange={(e) => handleFieldChanged(index, "lotNumber", e.currentTarget.value)}
                    disabled={String.isNullOrWhiteSpace(row.filler) || row.locked}
                  />
                </td>
                {state.processes.length > 1 && (
                  <td className="align-middle p-0">
                    {index === 0 && (
                      <FontAwesomeIcon
                        icon={faArrowDown}
                        size="sm"
                        type="button"
                        title="Copy"
                        onClick={() => handleCopy("lotNumber", row.lotNumber)}
                      />
                    )}
                  </td>
                )}
                <td>
                  <AssetNumberLookup
                    value={row.assetNumber}
                    process={row.process}
                    onChange={(e) => handleFieldChanged(index, "assetNumber", e.currentTarget.value)}
                    disabled={String.isNullOrWhiteSpace(row.process) || row.locked}
                  />
                </td>
                {state.processes.length > 1 && (
                  <td className="align-middle p-0">
                    {index === 0 && (
                      <FontAwesomeIcon
                        icon={faArrowDown}
                        size="sm"
                        type="button"
                        title="Copy"
                        onClick={() => handleCopy("assetNumber", row.assetNumber)}
                      />
                    )}
                  </td>
                )}
                <td>
                  <div className="form-check form-switch d-flex justify-content-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={row.unfinished}
                      onChange={(e) => handleFieldChanged(index, "unfinished", e.currentTarget.checked)}
                      disabled={
                        row.finished ||
                        row.locked ||
                        (row.process !== "Mechanical" && (!row.assetNumber || !row.filler || !row.lotNumber))
                      }
                    />
                  </div>
                </td>
                <td>
                  <div className="form-check form-switch d-flex justify-content-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={row.finished}
                      onChange={(e) => handleFieldChanged(index, "finished", e.currentTarget.checked)}
                      disabled={
                        row.unfinished ||
                        row.locked ||
                        (row.process !== "Mechanical" && (!row.assetNumber || !row.filler || !row.lotNumber))
                      }
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PageControls showBack allowBack={!save.isLoading}>
        <PageButton onClick={handleSave} loading={save.isLoading}>
          Save
        </PageButton>
        <PageButton onClick={handleSaveAndFinish} loading={save.isLoading}>
          Save and Finish
        </PageButton>
      </PageControls>
    </>
  );
};
